// Forgiving settings search shared by the player search bar and the admin
// defaults editor.
//
// Priorities:
//   1. label matches (best),
//   2. keywords/synonyms,
//   3. explanations,
//   4. id/category/default text,
//   5. fuzzy typo matches.
//
// A multi-word query returns all-token matches first, then any-token matches,
// so a narrow query still shows something related instead of an empty list.

import { categoryLabel, formatDefault, type SettingDef } from "./registry";

const SYNONYMS: Record<string, string> = {
  colour: "color",
  colours: "color",
  behavior: "behaviour",
  centre: "center",
  grey: "gray",
  metre: "meter",
  armour: "armor",
  defence: "defense",
  sens: "sensitivity",
  sensi: "sensitivity",
  mouse: "sensitivity",
  reticle: "crosshair",
  fps: "fps frame",
  ping: "latency network",
  lag: "latency network",
};

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function words(text: string): string[] {
  return normalize(text).split(" ").filter(Boolean);
}

function expand(token: string): string[] {
  const base = normalize(token);
  const extra = SYNONYMS[base];
  return extra ? [base, ...extra.split(" ")] : [base];
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    prev = cur;
  }
  return prev[b.length];
}

function fuzzyHit(token: string, hayWords: string[]): boolean {
  if (token.length < 4) return false;
  const max = token.length >= 7 ? 2 : 1;
  for (const word of hayWords) {
    if (word.length < Math.max(3, token.length - max)) continue;
    if (levenshtein(token, word) <= max) return true;
  }
  return false;
}

interface Scored {
  def: SettingDef;
  score: number;
  all: boolean;
}

function scoreDef(def: SettingDef, queryTokens: string[]): Scored | null {
  const label = normalize(def.label);
  const labelWords = words(def.label);
  const keywords = normalize((def.keywords ?? []).join(" "));
  const keywordWords = words(keywords);
  const help = normalize(def.help ?? "");
  const helpWords = words(def.help ?? "");
  const meta = normalize(
    `${def.id} ${categoryLabel(def.category)} ${formatDefault(def)} ${(def.options ?? [])
      .map((o) => `${o.value} ${o.label}`)
      .join(" ")}`,
  );
  const metaWords = words(meta);

  let total = 0;
  let hits = 0;
  for (const rawToken of queryTokens) {
    let best = 0;
    for (const token of expand(rawToken)) {
      if (label === token) best = Math.max(best, 160);
      else if (label.startsWith(token)) best = Math.max(best, 140);
      else if (label.includes(token)) best = Math.max(best, 120);
      if (keywordWords.some((w) => w === token || w.startsWith(token))) best = Math.max(best, 90);
      else if (keywords.includes(token)) best = Math.max(best, 70);
      if (helpWords.some((w) => w.startsWith(token))) best = Math.max(best, 55);
      else if (help.includes(token)) best = Math.max(best, 45);
      if (metaWords.some((w) => w.startsWith(token))) best = Math.max(best, 35);
      else if (meta.includes(token)) best = Math.max(best, 25);
      if (best === 0 && fuzzyHit(token, [...labelWords, ...keywordWords, ...helpWords])) {
        best = 18;
      }
    }
    if (best > 0) {
      hits++;
      total += best;
    }
  }
  if (hits === 0) return null;
  return { def, score: total, all: hits === queryTokens.length };
}

export function searchSettings(defs: SettingDef[], rawQuery: string): SettingDef[] {
  const queryTokens = words(rawQuery);
  if (queryTokens.length === 0) return [];
  const scored: Scored[] = [];
  for (const def of defs) {
    const result = scoreDef(def, queryTokens);
    if (result) scored.push(result);
  }
  const all = scored.filter((s) => s.all);
  const pool = all.length > 0 ? all : scored;
  pool.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.def.label.localeCompare(b.def.label);
  });
  return pool.map((s) => s.def);
}
